import type { CompileResult } from '../engine/types';
import { templateParts, useI18n, type MessageKey } from '../i18n';
import { TONES, TONE_GLYPH, countTones, tonesShown, type RowTone } from '../lib/rowStatus';
import { sizeName } from '../lib/sizes';

/**
 * How each tone looks (ADR-013, decision 6), shape and colour together: lichen for a verified count, honey for a
 * count taken on trust (dotted) or read by island and not confirmed (dashed), amber for an unread row with
 * proposals (a solid honey tint and « ◇ »), light wood for a row not read (« ? »). `glyph`: the glyph's ink;
 * `bar`: the bar of the visual; `ink`: the count at the end of the bar; `cell` and `tint`: a table cell's text
 * and background (the selected column keeps its own highlight instead of the tint).
 */
export const TONE_STYLE: Record<RowTone, { glyph: string; bar: string; ink: string; cell: string; tint: string }> = {
  checked: { glyph: '', bar: 'bg-success', ink: 'font-semibold text-fg', cell: 'text-success', tint: '' },
  unverified: {
    glyph: 'text-warning',
    bar: 'border border-dotted border-warning bg-warning/[0.14]',
    ink: 'font-semibold text-muted',
    cell: 'text-muted',
    tint: 'bg-warning/[0.06]',
  },
  island: {
    glyph: 'text-warning',
    bar: 'border border-dashed border-warning bg-warning/[0.14]',
    ink: 'font-semibold text-muted',
    cell: 'text-muted',
    tint: 'bg-warning/[0.06]',
  },
  proposals: { glyph: 'text-warning', bar: 'bg-warning/50', ink: 'font-semibold text-warning', cell: 'text-warning', tint: 'bg-warning/[0.10]' },
  unread: { glyph: 'text-subtle', bar: 'bg-fg/20', ink: 'font-semibold text-subtle', cell: 'text-subtle', tint: '' },
};

/** The « ~ », « ≈ », « ◇ » or « ? » before a count (nothing for a checked one); the explanation sits on the cell's tooltip. */
export function ToneGlyph({ tone, className = '' }: { tone: RowTone | null; className?: string }) {
  if (!tone || tone === 'checked') return null;
  return (
    <span aria-hidden="true" className={`select-none font-bold ${TONE_STYLE[tone].glyph} ${className}`} data-status-glyph={tone}>
      {TONE_GLYPH[tone]}
    </span>
  );
}

const COUNT_KEYS: Record<RowTone, [MessageKey, MessageKey]> = {
  checked: ['rows.checked1', 'rows.checkedN'],
  unverified: ['rows.unverified1', 'rows.unverifiedN'],
  island: ['rows.island1', 'rows.islandN'],
  proposals: ['rows.proposals1', 'rows.proposalsN'],
  unread: ['rows.unread1', 'rows.unreadN'],
};

/**
 * « 6 vérifiés · 1 non vérifié · 1 lu par îlot · 2 non lus » for the selected size (or the weakest tone per row
 * across sizes). Rows read by island and rows with proposals are counted only when there are some, so a result
 * without them reads as before.
 */
export function StatusSummary({ result, selectedSize, className = '' }: { result: CompileResult; selectedSize: number | null; className?: string }) {
  const { t, lang } = useI18n();
  const c = countTones(result.rows, selectedSize);
  const size = selectedSize === null ? t('diag.allSizes') : sizeName(result, selectedSize, lang);
  // Singular up to one, as French writes « 0 rang » (English has one form).
  const parts = TONES.filter((tone) => c[tone] > 0 || tone === 'checked' || tone === 'unverified' || tone === 'unread').map((tone) =>
    t(COUNT_KEYS[tone][c[tone] <= 1 ? 0 : 1], { n: c[tone] }),
  );
  // Each count holds together; the line may wrap between them (five of them do not fit a phone).
  return (
    <span className={`tabular text-2xs text-muted ${className}`} title={t('rows.statusSummaryTitle', { size })} data-testid="status-summary">
      {templateParts(lang, 'rows.statusSummary').map((part, i) =>
        typeof part === 'string' ? (
          part
        ) : part.name === 'size' ? (
          <span key={i} className="whitespace-nowrap">
            {size}
          </span>
        ) : (
          parts.map((p, j) => (
            <span key={`${i}-${j}`}>
              {j > 0 && ' · '}
              <span className="whitespace-nowrap">{p}</span>
            </span>
          ))
        ),
      )}
    </span>
  );
}

const LEGEND_KEYS: Record<RowTone, MessageKey> = {
  checked: 'rows.legendChecked',
  unverified: 'rows.legendUnverified',
  island: 'rows.legendIsland',
  proposals: 'rows.legendProposals',
  unread: 'rows.legendUnread',
};

/** One line per reading state the result shows: a green bar for a verified count, then « ~ », « ≈ », « ◇ » and « ? ». */
export function StatusLegend({ result, className = '' }: { result: CompileResult; className?: string }) {
  const { t } = useI18n();
  return (
    <ul aria-label={t('rows.statusLegend')} className={`space-y-0.5 text-2xs text-subtle ${className}`} data-testid="status-legend">
      {tonesShown(result.rows).map((tone) => (
        <li key={tone} className="flex items-baseline gap-1.5" data-status={tone}>
          {tone === 'checked' ? (
            <span aria-hidden="true" className={`inline-block h-1.5 w-4 translate-y-[-1px] rounded-pill ${TONE_STYLE.checked.bar}`} />
          ) : (
            <span aria-hidden="true" className={`w-4 text-center font-bold ${TONE_STYLE[tone].glyph}`}>
              {TONE_GLYPH[tone]}
            </span>
          )}
          <span>{t(LEGEND_KEYS[tone])}</span>
        </li>
      ))}
    </ul>
  );
}
